/**
 * Legal pages. The text is CMS-owned (a `page` with the matching slug); when the
 * page is missing we render the section frame with a clear editorial notice
 * rather than inventing legal wording.
 */
import { el, fromTrustedHtml } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs } from "../components/common.js";
import type { Page, PageBlock } from "../data/types.js";

async function legalPage(
	ctx: AppContext,
	slug: string,
	fallbackTitle: string,
): Promise<HTMLElement> {
	const copy = ctx.copy();
	const page = await ctx.gateway.page(slug);
	const title = page?.title ?? fallbackTitle;

	applyPageSeo(ctx, { title: `${title} — ${copy.brand.name}`, description: title });

	const body = el("div", { class: "mb-prose mb-prose--article" });
	if (page) {
		for (const block of page.blocks) body.appendChild(renderLegalBlock(block));
	} else {
		body.appendChild(
			el(
				"p",
				null,
				ctx.locale() === "en"
					? "This document is maintained in the content management system and has not been published yet."
					: "Ten dokument jest utrzymywany w systemie zarządzania treścią i nie został jeszcze opublikowany.",
			),
		);
	}

	return el(
		"div",
		{ class: "mb-view mb-view--legal" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"article",
				{ class: "mb-section__inner mb-article" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: title }]),
				el("h1", { class: "mb-article__title" }, title),
				body,
			),
		),
	);
}

function renderLegalBlock(block: PageBlock): Node {
	if (block.__typename === "RichTextBlock") return fromTrustedHtml(block.html);
	if (block.__typename === "HeroBlock") {
		return el(
			"div",
			null,
			el("h2", null, block.heading),
			block.subheading ? el("p", null, block.subheading) : null,
		);
	}
	// Legal pages only ever use text blocks; anything else is ignored on purpose.
	return document.createTextNode("");
}

export function privacyView(ctx: AppContext): Promise<HTMLElement> {
	return legalPage(
		ctx,
		ctx.locale() === "en" ? "privacy" : "prywatnosc",
		ctx.locale() === "en" ? "Privacy policy" : "Polityka prywatności",
	);
}

export function cookiesView(ctx: AppContext): Promise<HTMLElement> {
	return legalPage(ctx, "cookies", ctx.locale() === "en" ? "Cookie policy" : "Polityka cookies");
}

export type { Page };
